import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Menu,
  X,
  Globe,
  Home,
  Lightbulb,
  Map,
  FolderClosed,
  BookOpenText,
  Users,
  Settings,
  LogOut,
  Lock,
  ChevronRight,
  ShieldCheck,
  Gift,
  ShoppingBag
} from 'lucide-react';
import { useLab } from './LabContext';
import IntroVideoGate from './components/IntroVideoGate';

// Read the parent's currently-selected child id from localStorage and
// keep it reactive across tabs + same-tab updates from ParentDashboard
// (which dispatches `nimnyLab:selectedStudentChanged`).
const PARENT_SELECTED_STUDENT_KEY = 'nimnyLab:parentSelectedStudentId:v1';
const useParentSelectedStudentId = () => {
  const [sid, setSid] = useState(() => {
    if (typeof window === 'undefined') return null;
    try { return window.localStorage.getItem(PARENT_SELECTED_STUDENT_KEY) || null; }
    catch (_e) { return null; }
  });
  useEffect(() => {
    const onChange = () => {
      try {
        setSid(window.localStorage.getItem(PARENT_SELECTED_STUDENT_KEY) || null);
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener('storage', onChange);
    window.addEventListener('nimnyLab:selectedStudentChanged', onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('nimnyLab:selectedStudentChanged', onChange);
    };
  }, []);
  return sid;
};

// Derive the active child id from the current URL pathname. Recognises
// both the dynamic `/lab/student/<sid>/...` paths AND the legacy
// `/lab/max/...` / `/lab/anita/...` slugs so a parent who navigates
// directly (or refreshes) on any child-scoped page sees the Drawer
// "Мой путь" / "Лавка Нимни" rows jump to that same child.
const LEGACY_CHILD_ROUTE_SLUGS = ['max', 'anita'];
const studentIdFromPath = (pathname) => {
  if (typeof pathname !== 'string') return null;
  const m1 = pathname.match(/^\/lab\/student\/([^/]+)/);
  if (m1) return m1[1];
  const m2 = pathname.match(/^\/lab\/([^/]+)/);
  if (m2 && LEGACY_CHILD_ROUTE_SLUGS.includes(m2[1])) return m2[1];
  return null;
};

// ---------- Background ----------
// Deep night blue + warm gold star sparks. Lighter than public Privacy/Terms
// stars, since Lab is its own emotional space.
export const LabBackground = () => (
  <>
    <style>{`
      .lab-bg-base {
        position: fixed;
        inset: 0;
        z-index: -3;
        background:
          radial-gradient(60% 40% at 50% 0%, rgba(212,165,116,0.10), transparent 60%),
          linear-gradient(180deg, #0a1024 0%, #0c1330 40%, #0a0f26 100%);
        pointer-events: none;
      }
      .lab-bg-stars {
        position: fixed;
        inset: 0;
        z-index: -2;
        pointer-events: none;
        background-image:
          radial-gradient(1px 1px at 12% 18%, rgba(255,236,200,0.55), transparent),
          radial-gradient(1px 1px at 28% 42%, rgba(255,236,200,0.40), transparent),
          radial-gradient(1.5px 1.5px at 65% 22%, rgba(255,210,150,0.55), transparent),
          radial-gradient(1px 1px at 78% 58%, rgba(255,236,200,0.45), transparent),
          radial-gradient(1px 1px at 42% 72%, rgba(255,236,200,0.35), transparent),
          radial-gradient(1.5px 1.5px at 88% 80%, rgba(255,210,150,0.50), transparent),
          radial-gradient(1px 1px at 8% 82%, rgba(255,236,200,0.45), transparent),
          radial-gradient(1px 1px at 55% 90%, rgba(255,236,200,0.35), transparent);
        background-repeat: no-repeat;
        background-size: 100% 100%;
      }
    `}</style>
    <div className="lab-bg-base" aria-hidden="true" />
    <div className="lab-bg-stars" aria-hidden="true" />
  </>
);

// ---------- Reusable Card ----------
export const Card = ({ children, className = '', ...rest }) => (
  <div
    className={
      'rounded-2xl border border-amber-300/15 bg-[#0e1530]/70 backdrop-blur-sm shadow-[0_0_40px_-20px_rgba(212,165,116,0.35)] ' +
      className
    }
    {...rest}
  >
    {children}
  </div>
);

// ---------- Privacy meta tag (noindex on every Lab route) ----------
// Mount once at LabApp level. Adds <meta name="robots" content="noindex...">
// and removes it on unmount so the public site keeps its normal index status
// when the user navigates away from /lab/*.
export const NoIndex = () => {
  useEffect(() => {
    const id = 'lab-noindex-meta';
    const previous = document.querySelector('meta[name="robots"]');
    const previousContent = previous && previous.id !== id ? previous.getAttribute('content') : null;

    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement('meta');
      tag.id = id;
      tag.name = 'robots';
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow, noarchive');

    // If a public meta robots existed, hide it while we're inside /lab so
    // crawlers see ours; restore on unmount.
    if (previous && previous.id !== id) {
      previous.setAttribute('data-lab-paused-content', previousContent || '');
      previous.setAttribute('content', 'noindex, nofollow, noarchive');
    }

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
      // Restore any previous public robots meta we paused.
      const paused = document.querySelector('meta[name="robots"][data-lab-paused-content]');
      if (paused) {
        paused.setAttribute('content', paused.getAttribute('data-lab-paused-content') || '');
        paused.removeAttribute('data-lab-paused-content');
      }
    };
  }, []);
  return null;
};

// ---------- Nimny illustration (site avatar with warm halo) ----------
const NIMNY_AVATAR_URL = '/assets/nimny-avatar.webp';

export const NimnyArt = ({ size = 'lg', alt = 'Nimny' }) => {
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-28 h-28',
    lg: 'w-44 h-44 sm:w-56 sm:h-56'
  };
  const cls = sizes[size] || sizes.lg;
  return (
    <div className={`relative ${cls} flex items-center justify-center`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(closest-side, rgba(212,165,116,0.35), rgba(212,165,116,0.08) 60%, transparent 75%)',
          filter: 'blur(2px)'
        }}
        aria-hidden="true"
      />
      <img
        src={NIMNY_AVATAR_URL}
        alt={alt}
        className="relative w-full h-full object-cover rounded-full border-2 border-amber-200/25"
        style={{ transform: 'scale(1.15)', objectPosition: 'center top' }}
      />
    </div>
  );
};

// ---------- Max's personal avatar (real photo, used on his account card) ----------
const MAX_AVATAR_URL = '/assets/max-avatar.webp';

export const MaxAvatar = ({ size = 'sm', alt = 'Max' }) => {
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-28 h-28',
    lg: 'w-44 h-44 sm:w-56 sm:h-56'
  };
  const cls = sizes[size] || sizes.sm;
  return (
    <div className={`relative ${cls} flex items-center justify-center`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(closest-side, rgba(212,165,116,0.30), rgba(212,165,116,0.06) 60%, transparent 75%)',
          filter: 'blur(2px)'
        }}
        aria-hidden="true"
      />
      <img
        src={MAX_AVATAR_URL}
        alt={alt}
        className="relative w-full h-full object-cover rounded-full border-2 border-amber-200/25"
        style={{ objectPosition: 'center 35%' }}
      />
    </div>
  );
};

// ---------- Anita's personal avatar (real photo, used on her account card) ----------
const ANITA_AVATAR_URL = '/assets/anita-avatar.webp';

export const AnitaAvatar = ({ size = 'sm', alt = 'Anita' }) => {
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-28 h-28',
    lg: 'w-44 h-44 sm:w-56 sm:h-56'
  };
  const cls = sizes[size] || sizes.sm;
  return (
    <div className={`relative ${cls} flex items-center justify-center`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(closest-side, rgba(212,165,116,0.30), rgba(212,165,116,0.06) 60%, transparent 75%)',
          filter: 'blur(2px)'
        }}
        aria-hidden="true"
      />
      <img
        src={ANITA_AVATAR_URL}
        alt={alt}
        className="relative w-full h-full object-cover rounded-full border-2 border-amber-200/25"
        style={{ objectPosition: 'center 35%' }}
      />
    </div>
  );
};

// ---------- Header (used on every authenticated Lab screen) ----------
export const LabHeader = ({ onOpenMenu }) => {
  const { t, toggleLanguage, language } = useLab();
  return (
    <header className="relative z-20 px-5 pt-5">
      <div className="flex items-center justify-between rounded-full border border-amber-300/15 bg-[#0e1530]/60 backdrop-blur-sm px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-amber-200/70 font-mono">
          <span className="opacity-80">readnimny.com/lab</span>
          <span className="text-amber-200/40">—</span>
          <span className="text-amber-200/60">{t('privateBadge')}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Link to="/lab" className="flex items-center gap-2" data-testid="lab-brand">
          <NimnyArt size="sm" />
          <span
            className="text-xl text-[#f5ebe0]/90 lab-brand-mark"
          >
            {t('brand')}
          </span>
        </Link>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-300/20 text-amber-200/80 text-sm font-sans hover:bg-amber-300/5 transition-colors"
            data-testid="lab-lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            <span>{language === 'ru' ? 'EN' : 'RU'}</span>
          </button>
          <button
            type="button"
            onClick={onOpenMenu}
            className="p-2 rounded-full border border-amber-300/20 text-amber-200/80 hover:bg-amber-300/5 transition-colors"
            data-testid="lab-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

// ---------- Drawer (right-side menu) ----------
const MenuRow = ({ icon: Icon, label, onClick, to, testid }) => {
  const inner = (
    <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-amber-300/5 transition-colors">
      <span className="w-9 h-9 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <span className="text-[#f5ebe0]/85 font-sans">{label}</span>
    </div>
  );
  if (to)
    return (
      <Link to={to} data-testid={testid} onClick={onClick}>
        {inner}
      </Link>
    );
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left"
      data-testid={testid}
    >
      {inner}
    </button>
  );
};

export const Drawer = ({ open, onClose }) => {
  const { t, user, signOut } = useLab();
  const navigate = useNavigate();
  const location = useLocation();

  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const persistedSid = useParentSelectedStudentId();
  const routeSid = studentIdFromPath(location.pathname);
  // Active child id for parent navigation rows. Route wins so a direct
  // navigation to /lab/student/anita/path correctly shows Anita's links
  // even when localStorage was just cleared by /lab/parent.
  const activeChildSid = routeSid || persistedSid || null;

  // For parent navigation rows that target a child (Мой путь, Лавка Нимни),
  // fall back to /lab/parent when no child is currently active so the
  // family overview is always one tap away.
  const parentChildLink = (suffix) =>
    activeChildSid
      ? `/lab/student/${activeChildSid}${suffix}`
      : '/lab/parent';

  // Legacy children keep /lab/max or /lab/anita. New children created via
  // the parent admin live under /lab/student/<studentId>/*.
  const LEGACY_CHILD_IDS = ['max', 'anita'];
  const childBase = LEGACY_CHILD_IDS.includes(user?.studentId)
    ? `/lab/${user.studentId}`
    : user?.studentId
      ? `/lab/student/${user.studentId}`
      : '/lab/me';

  const handleLogout = async () => {
    await signOut();
    onClose?.();
    navigate('/lab/login');
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/60 backdrop-blur-[2px] transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`fixed top-0 right-0 z-40 h-full w-[88%] max-w-md bg-[#0a0f26]/95 backdrop-blur-md border-l border-amber-300/15 transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        } overflow-y-auto`}
        data-testid="lab-drawer"
      >
        <div className="px-5 pt-5 pb-4 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full border border-amber-300/20 text-amber-200/80 hover:bg-amber-300/5"
            aria-label="Close menu"
            data-testid="lab-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User card */}
        <div className="px-5">
          <Card className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-300/8 border border-amber-300/20 flex items-center justify-center text-amber-200/90 font-sans text-lg">
              {user?.name?.[0] || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#f5ebe0]/95 font-sans text-base">
                {user?.name || '—'}
              </div>
              <div className="text-amber-200/55 text-xs font-sans">
                {t('menuPersonalAccount')}
              </div>
              <div className="text-amber-200/40 text-xs font-sans mt-0.5">
                {t('menuProgressSavedOnly')}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-200/40" />
          </Card>
        </div>

        <div className="px-3 mt-5">
          {isParent ? (
            <>
              <MenuRow
                icon={Home}
                label={t('menuHome')}
                to="/lab/parent"
                onClick={onClose}
                testid="drawer-parent-home"
              />
              <MenuRow
                icon={Users}
                label={t('menuParentUsers')}
                to="/lab/parent/users"
                onClick={onClose}
                testid="drawer-parent-users"
              />
              <MenuRow
                icon={Gift}
                label={t('menuPrizeRequests')}
                to="/lab/parent/prize-requests"
                onClick={onClose}
                testid="drawer-parent-prize-requests"
              />
              <MenuRow
                icon={Map}
                label={t('menuMyPath')}
                to={parentChildLink('/path')}
                onClick={onClose}
                testid="drawer-my-path"
              />
              <MenuRow
                icon={ShoppingBag}
                label={t('menuPrizeShop')}
                to={parentChildLink('/prize-shop')}
                onClick={onClose}
                testid="drawer-prize-shop"
              />
            </>
          ) : (
            <>
              <MenuRow
                icon={Home}
                label={t('menuHome')}
                to={childBase}
                onClick={onClose}
                testid="drawer-child-home"
              />
              <MenuRow
                icon={Lightbulb}
                label={t('menuTodayMission')}
                to={`${childBase}/week-1/day-1`}
                onClick={onClose}
                testid="drawer-today-mission"
              />
              <MenuRow
                icon={Map}
                label={t('menuMyPath')}
                to={`${childBase}/path`}
                onClick={onClose}
                testid="drawer-my-path"
              />
              <MenuRow
                icon={ShoppingBag}
                label={t('menuPrizeShop')}
                to={`${childBase}/prize-shop`}
                onClick={onClose}
                testid="drawer-child-prize-shop"
              />
              <MenuRow
                icon={FolderClosed}
                label={t('menuMyProjects')}
                to={`${childBase}/projects`}
                onClick={onClose}
                testid="drawer-my-projects"
              />
              <MenuRow
                icon={BookOpenText}
                label={t('menuWordChest')}
                to={`${childBase}/word-chest`}
                onClick={onClose}
                testid="drawer-word-chest"
              />
              <MenuRow
                icon={Users}
                label={t('menuParentRoom')}
                to="/lab/parent"
                onClick={onClose}
                testid="drawer-parent-room"
              />
            </>
          )}
        </div>

        <div className="px-3 mt-3 pt-3 border-t border-amber-300/10">
          <MenuRow
            icon={Settings}
            label={t('menuSettings')}
            to={isParent ? '/lab/parent/settings' : `${childBase}/settings`}
            onClick={onClose}
            testid="drawer-settings"
          />
          <MenuRow
            icon={LogOut}
            label={t('menuLogout')}
            onClick={handleLogout}
            testid="drawer-logout"
          />
        </div>

        {/* Privacy footer card */}
        <div className="px-5 mt-5 mb-8">
          <Card className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85 shrink-0">
                <ShieldCheck className="w-[18px] h-[18px]" />
              </div>
              <div className="text-sm">
                <div className="text-[#f5ebe0]/90 font-sans">
                  {t('privateFamily')}
                </div>
                <div className="text-amber-200/55 mt-1 font-serif leading-relaxed">
                  {t('privateFamilyDesc')}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </aside>
    </>
  );
};

// ---------- Layout wrapper that includes header + drawer ----------
// `desktopWide` opts the page into the wider desktop container (≈1152px at lg).
// All other pages keep the original mobile-first `max-w-md` look unchanged.
export const LabLayout = ({ children, contained = true, desktopWide = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wideCls = desktopWide ? 'max-w-md lg:max-w-6xl' : 'max-w-md';
  const padCls = desktopWide ? 'px-5 lg:px-8' : 'px-5';
  return (
    <div className="min-h-screen relative text-[#f5ebe0]">
      <LabBackground />
      <NoIndex />
      <LabHeader onOpenMenu={() => setMenuOpen(true)} />
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      <IntroVideoGate />
      <main
        className={
          `relative z-10 ${padCls} pb-16 pt-6 ` +
          (contained ? `${wideCls} mx-auto` : '')
        }
      >
        {children}
      </main>
      <footer
        className="relative z-10 px-5 pb-8 pt-2 text-center"
        data-testid="lab-copyright"
      >
        <p className="text-amber-200/30 font-sans text-xs">
          © 2026 Nimny LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

// ---------- Status pill / lock icon helpers ----------
export const StatusPill = ({ status, t }) => {
  const map = {
    open: { label: t('statusOpen'), cls: 'text-amber-200/85 bg-amber-300/10 border-amber-300/25' },
    in_progress: {
      label: t('statusInProgress'),
      cls: 'text-amber-100 bg-amber-300/15 border-amber-300/35'
    },
    completed: {
      label: t('statusCompleted'),
      cls: 'text-emerald-200 bg-emerald-400/10 border-emerald-300/25'
    },
    locked: {
      label: t('statusLocked'),
      cls: 'text-amber-200/40 bg-amber-300/5 border-amber-300/15'
    }
  };
  const m = map[status] || map.locked;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border font-sans ${m.cls}`}
    >
      {m.label}
    </span>
  );
};

export const LockGlyph = () => (
  <span className="w-9 h-9 rounded-full bg-amber-300/5 border border-amber-300/15 flex items-center justify-center text-amber-200/40">
    <Lock className="w-[16px] h-[16px]" />
  </span>
);

// Small label rendered on child-scoped pages when the current user is a
// parent / super_admin previewing the course. Communicates clearly that
// progress and rewards will not be touched by simply viewing.
export const ParentPreviewBadge = () => {
  const { user, language } = useLab();
  if (!user) return null;
  if (user.role !== 'parent' && user.role !== 'super_admin') return null;
  const label = language === 'ru'
    ? 'Режим просмотра родителя'
    : 'Parent preview mode';
  return (
    <div
      className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-200 text-xs font-sans px-3 py-1"
      data-testid="parent-preview-badge"
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      {label}
    </div>
  );
};

// Convenience hook: are we a parent / super_admin?
export const useIsParentPreview = () => {
  const { user } = useLab();
  return !!(user && (user.role === 'parent' || user.role === 'super_admin'));
};

// ---------- Auth guard ----------
// Auth is server-verified (cookie). On mismatch we redirect — there is no
// in-app PIN escalation any more (parent / max / anita are separate accounts).
//
// IMPORTANT: while the initial /api/lab/auth/me round-trip is in flight
// (authLoading=true) we render nothing instead of redirecting. Otherwise a
// direct navigation to a protected URL would bounce to /lab/login, the
// session would be re-hydrated, and the login page would forward the user
// to their *home* — losing the original target path (e.g. /prize-shop).
const homeForChild = (user) => {
  if (!user) return '/lab';
  if (user.studentId === 'max' || user.studentId === 'anita') {
    return `/lab/${user.studentId}`;
  }
  if (user.studentId) return `/lab/student/${user.studentId}`;
  return '/lab';
};

export const RequireAuth = ({ role, children }) => {
  const { user, authLoading } = useLab();
  const location = useLocation();
  if (authLoading) return null;
  if (!user) {
    return (
      <RedirectTo
        to={{ pathname: '/lab/login', state: { from: location.pathname } }}
      />
    );
  }
  if (role && user.role !== role) {
    // Send the user to their own home — never render a parent-only subtree
    // for a child role, even briefly, so answer keys never enter the DOM.
    if (user.role === 'parent') return <RedirectTo to="/lab/parent" />;
    return <RedirectTo to={homeForChild(user)} />;
  }
  return children;
};

// Route guard for student-scoped pages: only the matching child (or any
// parent) can enter. Other children are redirected to their own home.
export const RequireStudent = ({ studentId, children }) => {
  const { user, authLoading } = useLab();
  const location = useLocation();
  if (authLoading) return null;
  if (!user) {
    return (
      <RedirectTo
        to={{ pathname: '/lab/login', state: { from: location.pathname } }}
      />
    );
  }
  if (user.role === 'parent') return children;
  if (user.studentId !== studentId) {
    return <RedirectTo to={homeForChild(user)} />;
  }
  return children;
};

// Dynamic counterpart for /lab/student/:studentId/* routes — the
// expected studentId comes from useParams() instead of being hardcoded.
// A child can only enter their own studentId; parents in the same
// family pass through.
export const RequireDynamicStudent = ({ children }) => {
  const { user, authLoading } = useLab();
  const params = useParams();
  const location = useLocation();
  const target = params.studentId;
  if (authLoading) return null;
  if (!user) {
    return (
      <RedirectTo
        to={{ pathname: '/lab/login', state: { from: location.pathname } }}
      />
    );
  }
  if (user.role === 'parent' || user.role === 'super_admin') return children;
  if (user.role !== 'child') return <RedirectTo to="/lab" />;
  if (user.studentId !== target) {
    return <RedirectTo to={homeForChild(user)} />;
  }
  return children;
};

const RedirectTo = ({ to }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof to === 'string') navigate(to, { replace: true });
    else navigate(to.pathname, { replace: true, state: to.state });
  }, [navigate, to]);
  return null;
};
